import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { NIBBLE_API_KEY } from '../../config/setup/setup';
import { NIBBLE_TIMER } from '../../config/text/text';
import { trackEvent } from '../../helpers/analytics';
import { createBasketAndMoveToCheckout } from '../../helpers/basketCreator';
import httpCommonHeaders from '../../helpers/httpCommonHeaders';
import { STATUS_ACTIVE } from '../../helpers/vipSubscribeHelper';

const NibbleWidget = ({
  deal,
  variantId,
  quantity = 1,
  setIsNibbleAppeared,
  cyo = false,
}) => {
  const userVipStatus = useSelector(
    (state) => state.user.userprofile?.userVipStatus,
  );
  const nibbleButtonRef = useRef(null);
  const isEventListenerAttached = useRef(false); // Track if event listeners are attached
  const [locationStore] = useSelector((state) => [state.locations]);
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  const router = useRouter();

  const negotiationType = 'product';
  const selectedProduct = deal?.products?.find(
    (product) => product.id === (variantId || deal?.products[0]?.id),
  );

  const productTags = selectedProduct?.nibbleWidgetTags
    ?.filter((tag) => {
      if (userVipStatus === STATUS_ACTIVE) {
        return tag.vip === true;
      }

      return tag.vip !== true;
    })
    .map((tag) => `category:${tag.name}`)
    .filter(Boolean)
    .join(',');

  const fetchAPI = async (url, body) => {
    try {
      const response = await fetch(url, {
        body: JSON.stringify(body),
        credentials: 'include',
        headers: httpCommonHeaders(),
        method: 'POST',
      });

      return await response.json();
    } catch (error) {
      console.error('API Fetch Error:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (
      nibbleButtonRef?.current !== null &&
      typeof setIsNibbleAppeared === 'function'
    ) {
      setIsNibbleAppeared(true);
    }
  }, [nibbleButtonRef]);

  const handleNibbleStart = useCallback(
    async (event) => {
      trackEvent('nibble_haggle_click');
      const [successCallback, errorCallback] = event.detail;
      try {
        const response = await fetchAPI(
          `${process.env.NEXT_PUBLIC_VENDOR_GATEWAY_URL}/create`,
          {
            dealId: deal.id,
            productId: variantId ? variantId : deal?.products[0]?.id,
            quantity: quantity || 1,
          },
        );
        successCallback(response);
      } catch {
        errorCallback('Error creating Nibble');
      }
    },
    [deal, variantId],
  );

  const handleSessionEnd = useCallback(
    async (event) => {
      const [nibbleId, sessionStatus, finalizeCallback] = event.detail;
      finalizeCallback({ close: true });

      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_VENDOR_GATEWAY_URL}/get/${nibbleId}`,
        );
        if (!response.ok) throw new Error('Failed to fetch nibble data');

        const data = await response.json();
        localStorage.removeItem(NIBBLE_TIMER);

        if (data?.sessionStatus === 'successful') {
          const currentTime = new Date();
          const reserveTime = new Date(currentTime.getTime() + 15 * 60_000);

          const nibbleData = {
            negotiatedPriceId: data.nibbleId,
            quantity: data.quantity,
            reservePrice: Number(
              (data.negotiatedPrice / data.quantity).toFixed(2),
            ),
            reserveTime,
          };

          const position = (() => {
            if (!Array.isArray(deal.products)) {
              console.error('Invalid or missing products array.');

              return 0;
            }
            const index = deal.products.findIndex(
              (product) => product.id === Number(data.dealProductId),
            );

            return index === -1 ? 0 : index;
          })();

          trackEvent('nibble_buy_click');

          await createBasketAndMoveToCheckout(
            deal,
            locationStore,
            position,
            false,
            false,
            isAuthenticated,
            userVipStatus,
            router,
            nibbleData,
          );
        }
      } catch (error) {
        console.error('Error in handleSessionEnd:', error);
      }
    },
    [deal, locationStore, isAuthenticated, userVipStatus, router],
  );

  const initializeNibbleButton = useCallback(() => {
    if (!nibbleButtonRef.current || isEventListenerAttached.current) return;

    const button = nibbleButtonRef.current;
    button.addEventListener('nibble-session-start', handleNibbleStart);
    button.addEventListener('nibble-session-end', handleSessionEnd);
    isEventListenerAttached.current = true; // Mark listeners as attached
  }, [handleNibbleStart, handleSessionEnd]);

  useEffect(() => {
    const scriptSource =
      'https://cdn.nibble.website/widget/nibble-button.min.js';

    if (!document.querySelector(`script[src="${scriptSource}"]`)) {
      const script = document.createElement('script');
      script.src = scriptSource;
      script.async = true;

      script.onload = () => {
        initializeNibbleButton();
      };

      script.onerror = (error) => {
        console.error('Error loading Nibble script:', error);
      };

      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
        if (nibbleButtonRef.current) {
          const button = nibbleButtonRef.current;
          button.removeEventListener('nibble-session-start', handleNibbleStart);
          button.removeEventListener('nibble-session-end', handleSessionEnd);
          isEventListenerAttached.current = false;
        }
      };
    } else {
      initializeNibbleButton();
    }
  }, [initializeNibbleButton]);

  return (
    <>
      <div className="nibble-widget">
        <nibble-button
          api-key={NIBBLE_API_KEY}
          negotiation-type={negotiationType}
          product-id={deal?.id}
          product-tags={productTags}
          ref={nibbleButtonRef}
          variant-id={variantId || deal?.products[0]?.id}
        />
      </div>
    </>
  );
};

NibbleWidget.propTypes = {
  deal: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.number]),
  variantId: PropTypes.oneOfType([PropTypes.string, PropTypes.string]),
};

NibbleWidget.defaultProps = {
  variantId: null,
};

export default NibbleWidget;
